import { Command } from "commander";
import {
	logger,
	type IAgentRuntime,
	type Character,
	type ProjectAgent,
	type Plugin,
	AgentRuntime,
	stringToUuid
} from "@elizaos/core";
import { loadProject } from "../project.js";
import { TestRunner } from "../test/runner.js";
import { AgentServer, type ServerOptions } from "../server/index.js";
import * as net from "node:net";
import ora from "ora";
import path from "node:path";
import * as os from "node:os";
import * as fs from "node:fs";

// Helper function to check port availability
async function checkPortAvailable(port: number): Promise<boolean> {
	return new Promise((resolve) => {
		const server = net.createServer();
		server.once("error", () => {
			resolve(false);
		});
		server.once("listening", () => {
			server.close();
			resolve(true);
		});
		server.listen(port);
	});
}

// Helper function to start an agent
async function startAgent(
	character: Character,
	server: AgentServer,
	plugins: Plugin[] = [],
	options: {
		dataDir?: string;
		postgresUrl?: string;
	} = {},
): Promise<IAgentRuntime> {
	character.id ??= stringToUuid(character.name);

	const runtime = new AgentRuntime({
		character,
		plugins,
	});

	// start services/plugins/process knowledge
	await runtime.initialize();

	// add to container
	server.registerAgent(runtime);

	// report to console
	logger.debug(`Started ${runtime.character.name} as ${runtime.agentId}`);

	return runtime;
}

const runAgentTests = async (options: {
	configure?: boolean;
	port?: number;
	character?: string;
	plugin?: string;
	skipPlugins?: boolean;
	skipProjectTests?: boolean;
}) => {
	let runtime: IAgentRuntime;
	let projectAgent: ProjectAgent | undefined;

	// Create a temporary directory for test data
	const tempDir = path.join(os.tmpdir(), `eliza-test-${Date.now()}`);
	const dataDir = path.join(tempDir, 'data', 'test');

	// Create the directory if it doesn't exist
	fs.mkdirSync(dataDir, { recursive: true });

	const serverOptions: ServerOptions = {
		dataDir,
		middlewares: [],
		skipDatabase: true
	};

	const server = new AgentServer(serverOptions);

	// Skip database initialization for tests
	try {
		logger.info("Skipping database initialization for tests");
	} catch (error) {
		logger.error("Failed to initialize server:", error);
		if (error instanceof Error) {
			logger.error("Error details:", error.message);
		}
		process.exit(1);
	}

	// Set up server properties
	server.startAgent = async (character) => {
		logger.info(`Starting agent for character ${character.name}`);
		return startAgent(character, server, [], { dataDir });
	};

	const serverPort = Number.parseInt(options.port?.toString() || "3000", 10);

	// Start the server
	let currentPort = serverPort;
	while (!(await checkPortAvailable(currentPort))) {
		logger.warn(`Port ${currentPort} is in use, trying ${currentPort + 1}`);
		currentPort++;
	}

	await server.start(currentPort);
	logger.success(`Test server started on port ${currentPort}`);

	try {
		const spinner = ora("Starting agent...").start();
		
		// Start the agent runtime
		if (options.character) {
			const character = await server.loadCharacterTryPath(options.character);
			runtime = await server.startAgent(character);
		} else {
			try {
				// Try to load project from current directory
				const project = await loadProject(".");
				if (project?.agent) {
					runtime = await server.startAgent(project.agent.character);
					projectAgent = project.agent;
				} else {
					throw new Error("No project agent found in current directory");
				}
			} catch (error) {
				logger.error("Failed to load project:", error);
				process.exit(1);
			}
		}

		spinner.succeed("Agent started");

		// Run tests
		const testRunner = new TestRunner(runtime, projectAgent);
		const results = await testRunner.runTests({
			filter: options.plugin,
			skipPlugins: options.skipPlugins,
			skipProjectTests: options.skipProjectTests
		});

		// Clean up
		await server.stop();
		process.exit(results.failed > 0 ? 1 : 0);
	} catch (error) {
		logger.error("Error running tests:", error);
		await server.stop();
		process.exit(1);
	}
};

const test = new Command("test")
	.description("Run tests for Eliza agent plugins")
	.option("-c, --configure", "Configure test settings")
	.option("-p, --port <number>", "Port to run test server on", "3000")
	.option("-C, --character <path>", "Path to character file")
	.option("-P, --plugin <name>", "Name of plugin to test")
	.option("--skip-plugins", "Skip plugin tests")
	.option("--skip-project-tests", "Skip project tests")
	.action(async (options) => {
		await runAgentTests(options);
	});

export { test };
export default test;